/**
 * Collect the files from a drag-and-drop or a folder pick — including every
 * file inside dropped FOLDERS (walked recursively), which `dataTransfer.files`
 * alone does not give. Operating-system clutter (`.DS_Store`, `Thumbs.db`,
 * `desktop.ini`, other dot-files) is skipped so a dropped library uploads only
 * its documents.
 *
 * The path each file had inside its folder ("Real Estate/Leases/Scan_0042.pdf")
 * is kept too — {@link relativePathOf} — because the folder is a classification
 * clue (ADR-0018 update 2026-10-06).
 */

/** Paths of files read out of DROPPED folders: the File objects a directory
 * walk returns carry an empty `webkitRelativePath`, so the walk records the
 * entry's `fullPath` here. Weak, so it never keeps a File alive. */
const droppedPaths = new WeakMap<File, string>();

/**
 * The path of `file` inside the folder it was picked or dropped from, its own
 * name included ("Real Estate/Leases/Scan_0042.pdf"), or undefined for a loose
 * file. A folder pick (`<input webkitdirectory>`) reports it as
 * `webkitRelativePath`; a dropped folder's walk records it. The server
 * normalizes it and keeps only the folder.
 */
export function relativePathOf(file: File): string | undefined {
  const path = droppedPaths.get(file) || file.webkitRelativePath || '';
  const trimmed = path.replace(/^\/+/, '');
  return trimmed.includes('/') ? trimmed : undefined;
}

/** OS/housekeeping files that are never documents. */
export function isJunkFile(name: string): boolean {
  const base = name.split(/[\\/]/).pop() ?? name;
  return base.startsWith('.') || /^(thumbs\.db|desktop\.ini)$/i.test(base);
}

/** Drop the housekeeping files from a picked or dropped list. */
export function documentFiles(files: Iterable<File>): File[] {
  return Array.from(files).filter((f) => !isJunkFile(f.name));
}

// The File and Directory Entries API (webkitGetAsEntry), typed minimally.
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  /** "/Folder/Sub/file.pdf" for an entry inside a dropped folder. */
  fullPath?: string;
}
interface FsFileEntry extends FsEntry {
  file: (ok: (f: File) => void, fail: (e: unknown) => void) => void;
}
interface FsDirectoryEntry extends FsEntry {
  createReader: () => {
    readEntries: (ok: (entries: FsEntry[]) => void, fail: (e: unknown) => void) => void;
  };
}

async function filesOfEntry(entry: FsEntry): Promise<File[]> {
  if (entry.isFile) {
    const file = await new Promise<File>((ok, fail) => (entry as FsFileEntry).file(ok, fail));
    if (entry.fullPath) droppedPaths.set(file, entry.fullPath);
    return [file];
  }
  if (!entry.isDirectory) return [];
  const reader = (entry as FsDirectoryEntry).createReader();
  const children: FsEntry[] = [];
  // readEntries returns the directory in batches; an empty batch ends it.
  for (;;) {
    const batch = await new Promise<FsEntry[]>((ok, fail) => reader.readEntries(ok, fail));
    if (batch.length === 0) break;
    children.push(...batch);
  }
  const nested = await Promise.all(children.map(filesOfEntry));
  return nested.flat();
}

/**
 * Every document file in a drop: walks dropped folders where the browser
 * supports it, else falls back to the plain file list.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = Array.from(dt.items ?? []);
  const entries = items
    .filter((i) => i.kind === 'file')
    .map((i) =>
      (i as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry?.(),
    );
  if (entries.length > 0 && entries.every((e) => e)) {
    try {
      const nested = await Promise.all((entries as FsEntry[]).map(filesOfEntry));
      return documentFiles(nested.flat());
    } catch {
      // Fall through to the flat list.
    }
  }
  return documentFiles(Array.from(dt.files ?? []));
}
